import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  signal,
  inject,
  ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { forkJoin, of } from "rxjs";
import { catchError, first, switchMap, tap } from "rxjs/operators";
import { FileSaverService } from "ngx-filesaver";
import { LinkGen } from "../../../../shared/constants/loren-routes.constants";
import { LorenDashboardCardsViewTypes } from "./enums/loren-dashboard-cards-view-types.enum";
import { LorenContentTypes } from "../../../../shared/services/loren-http-service/enums/loren-content-types.enums";
import { LorenFeature } from "../../../../shared/enums/loren-feature.enum";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import { LorenDashboardWidgetPreference } from "../../interfaces/loren-dashboard-widget-preference.interface";
import { LorenPrivilegeEntityType } from "../../../loren-roles/interfaces/loren-role.interface";
import { LorenBaseFeature } from "../../../../shared/base-components/loren-base-feature/loren-base-feature";
import {
  LorenAssetRefresherService
} from "../../../../shared/services/loren-asset-refresher-service/loren-asset-refresher.service";
import {
  LorenAuthenticationService
} from "../../../../shared/services/loren-authentication-service/loren-authentication.service";
import {
  LorenDetailsOutletService
} from "../../../../shared/services/loren-details-outlet-service/loren-details-outlet.service";
import { LorenScrollService } from "../../../../shared/services/loren-scroll-service/loren-scroll.service";
import { LorenSnackBarService } from "../../../../shared/services/loren-snack-bar-service/loren-snack-bar.service";
import { LorenUtilsService } from "../../../../shared/services/loren-utils-service/loren-utils.service";
import { LorenAssetService } from "../../../loren-assets/services/loren-asset-service/loren-asset.service";
import { LorenGroupService } from "../../../loren-groups/services/loren-group-service/loren-group.service";
import {
  LorenReportsService
} from "../../../loren-assets-reports/services/loren-reports-service/loren-reports.service";
import {
  LorenReportsUtilsService
} from "../../../loren-assets-reports/services/loren-reports-utils-service/loren-reports-utils.service";
import {
  LorenFeatureToggleService
} from "../../../../shared/services/loren-feature-toggle-service/loren-feature-toggle.service";
import {
  LorenDashboardWidgetsModalComponent
} from "../loren-dashboard-widgets-modal/loren-dashboard-widgets-modal.component";
import {
  LorenAdminRolesService
} from "../../../loren-admin/loren-admin-accounts/services/loren-admin-roles-service/loren-admin-roles.service";
import {
  LOREN_USER_CLIENT_ROLES
} from "../../../../shared/services/loren-authentication-service/constants/loren-user-roles.constants";
import {
  LorenErrorHandlingService
} from "../../../../shared/services/loren-error-handling-service/loren-error-handling-service.service";
import { GroupItemModel } from "../../../../shared/modules/loren-hierarchy-selector/group.interface";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import { AssetFilterService } from "../../../asset/asset-filter.service";

@Component({
  selector: "loren-dashboard",
  templateUrl: "./loren-dashboard.component.html",
  styleUrls: ["./loren-dashboard.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LorenDashboardComponent
  extends LorenBaseFeature
  implements OnInit, OnDestroy {
  dialog = inject(MatDialog);
  private router = inject(Router);
  private fileSaverService = inject(FileSaverService);
  private assetService = inject(LorenAssetService);
  private reportsService = inject(LorenReportsService);
  private groupService = inject(LorenGroupService);
  private adminRolesService = inject(LorenAdminRolesService);
  private assetRefresherService = inject(LorenAssetRefresherService);
  private scrollService = inject(LorenScrollService);
  private authService = inject(LorenAuthenticationService);
  private detailsOutletService = inject(LorenDetailsOutletService);
  private activatedRoute = inject(ActivatedRoute);
  private featureToggleService = inject(LorenFeatureToggleService);
  private lorenSnackBarService = inject(LorenSnackBarService);
  private errorHandlingService = inject(LorenErrorHandlingService);
  private assetFilterService = inject(AssetFilterService);

  @ViewChild(MatPaginator) paginator: MatPaginator;

  static readonly title = "features.dashboard";
  readonly viewTypes = LorenDashboardCardsViewTypes;
  readonly privileges = LorenPrivilegeEntityType;

  private assets = signal<LorenAsset[]>([]);
  private userId = signal(null);
  private searchValue = signal("");
  allCardModels = computed(() => {
    return this.preferences()
      ? this.assets().sort(
        (a, b) =>
          this.preferences().get(a.id)?.ordinal -
          this.preferences().get(b.id)?.ordinal
      )
      : this.assets();
  });

  activeGroupFilters = signal<number[]>([]);
  allowGroups = signal<boolean>(false);
  allowDeviceReport = signal<boolean>(false);
  currentPageIndex = signal<number>(0);
  currentViewType = signal<LorenDashboardCardsViewTypes>(
    LorenDashboardCardsViewTypes.GRID
  );
  groups = signal<GroupItemModel[]>([]);
  loadingData = signal<boolean>(false);
  error = signal<boolean>(false);
  preferences = signal<Map<
      number,
      LorenDashboardWidgetPreference
  >>(new Map());
  showCreateAssetState = signal<boolean>(null);
  totalNumOfAssets = signal<number>(null);

  assetsPerPage = computed(() =>
    this.currentViewType() === this.viewTypes.TABLE
      ? 20
      : this.isCustomXXL
        ? 10
        : 12
  );
  groupsFilter = computed(() =>
    this.groups().map((g) => ({ key: g.name, value: g.id }))
  );
  activeGroupFilterChips = computed(() =>
    this.groups().filter((g) => this.activeGroupFilters().includes(g.id))
  );

  ngOnInit(): void {
    this.loadingData.set(true);
    this.userId.set(this.authService.getUserInfo().id);
    this.getPreferences();

    // The saved filter gates the first asset load, otherwise the dashboard
    // renders unfiltered assets until it arrives.
    this.assetFilterService.get()
        .pipe(
            catchError(() => of({ groups: [] })),
            tap(({ groups }) => this.activeGroupFilters.set(groups)),
            switchMap(() => this.activatedRoute.queryParams),
            takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((params) => {
          this.searchValue.set(params.filterBy || "");
          this.getPagedAssets();
        });

    this.groupService.query()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          this.groups.set(res.items.filter((g) => g.name !== "All"));
          if (this.isHandset) {
            this.handleCardViewChange(LorenDashboardCardsViewTypes.GRID);
          }

          this.setRefresherServiceSubscriptions();
        });

    this.getAllowDeviceReport();
    this.getAllowGroups();

    const lastSelectedViewType =
      LorenUtilsService.getDashboardLastSelectedViewType(
        this.authService.getUserInfo().id
      );
    if (lastSelectedViewType) {
      const key = Object.keys(LorenDashboardCardsViewTypes).find(
        (x) => x === lastSelectedViewType
      );
      this.currentViewType.set(LorenDashboardCardsViewTypes[key]);
    }
  }

  ngOnDestroy(): void {
    this.detailsOutletService.toggleState(false);
  }

  openWidgetsDialog() {
    this.assetService.getAllAssets()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.error.set(false);
            const dialogData = res.items.map((i) => {
              const preference = this.preferences()?.get(i.id);
              return {
                ...i,
                groups: this.assets().find((a) => a.id === i.id)?.groups,
                show: this.preferences() ? preference?.show : true,
                ordinal: preference?.ordinal,
              };
            });

            this.prepareWidgetsDialog(dialogData)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((result) => {
                  if (!result) {
                    return;
                  }

                  const assetIdPreferenceMap = new Map<
                      number,
                      LorenDashboardWidgetPreference
                  >(
                      dialogData.map((x) => [
                        x.id,
                        {ordinal: x.ordinal, show: x.show},
                      ])
                  );
                  LorenUtilsService.setDashboardWidgetsPreferences(
                      this.userId(),
                      assetIdPreferenceMap
                  );
                  this.preferences.set(assetIdPreferenceMap);
                  this.getPagedAssets();

                  if (this.allCardModels().length === 0) {
                    this.lorenSnackBarService.showTranslatedSuccessMessage(
                        "dashboard.no_assets"
                    );
                  }

                  if (this.currentPageIndex() > 0) {
                    this.paginator?.firstPage();
                  }
                })
          },
          error: () => this.error.set(true),
        });
  }

  pageChange(event: PageEvent) {
    this.currentPageIndex.set(event.pageIndex);
    this.getPagedAssets();
    setTimeout(() => this.scrollService.scrollToTop(), 501);
  }

  handleCardViewChange(selectedView: LorenDashboardCardsViewTypes) {
    this.currentViewType.set(selectedView);
    LorenUtilsService.setDashboardLastSelectedViewType(
      selectedView,
      this.authService.getUserInfo().id
    );
    this.setRefresherServiceSubscriptions();
  }

  handleDefineAsset() {
    this.detailsOutletService.resetDetailsOutlet();
    this.detailsOutletService.toggleState(true);

    this.router.navigateByUrl(LinkGen.Assets.New());
  }

  downloadReport() {
    this.reportsService.exportDevicesReport().subscribe({
      next: (res: BlobPart) => {
        const blob = new Blob([res], {
          type: LorenContentTypes.EXCEL,
        });
        const filename = LorenReportsUtilsService.getDevicesReportFilename();
        this.fileSaverService.save(blob, filename);
      },
      error: () => this.errorHandlingService.showSnackbarError(),
    });
  }

  filter(e: Map<number, boolean>) {
    this.applyGroupFilters(Array.from(e.keys()).filter((k) => e.get(k)));
  }

  removeGroupFilter(groupId: number) {
    this.applyGroupFilters(this.activeGroupFilters().filter((id) => id !== groupId));
  }

  private applyGroupFilters(groups: number[]) {
    this.currentPageIndex.set(0);
    this.activeGroupFilters.set(groups);
    this.getPagedAssets();

    this.assetFilterService.set(groups)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (stored) => {
            if (stored.groups.join() !== this.activeGroupFilters().join()) {
              this.activeGroupFilters.set(stored.groups);
              this.getPagedAssets();
            }
          },
          error: () => this.errorHandlingService.showSnackbarError(),
        });
  }

  resize() {
    if (this.isHandset && this.currentViewType() !== this.viewTypes.GRID) {
      this.handleCardViewChange(
        this.isHandset ? this.viewTypes.GRID : this.currentViewType()
      );
    }
  }

  private setRefresherServiceSubscriptions() {
    this.assetRefresherService.refresh$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.getPagedAssets();
        })
  }

  private getPagedAssets() {
    const filteredAssets = this.activeGroupFilters().length
      ? Array.from(
        new Set(
          this.groups()
            .filter((group) => this.activeGroupFilters().includes(group.id))
            .flatMap((group) => group.assetsInGroup)
        )
      )
      : [];

    const assetsFromPreferences = this.preferences()
      ? Array.from(this.preferences().keys())
        .map((k) => (this.preferences().get(k)?.show ? k : null))
        .filter((a) => a)
        .sort(
          (a, b) =>
            this.preferences().get(a)?.ordinal -
            this.preferences().get(b)?.ordinal
        )
      : [];

    const hasSearchValue = this.searchValue() && this.searchValue().trim().length > 0;

    let finalAssets;
    if (hasSearchValue) {
      finalAssets = null;
    } else if (this.activeGroupFilters().length && this.preferences()) {
      finalAssets = assetsFromPreferences.filter((a) => filteredAssets.includes(a));
    } else if (this.activeGroupFilters().length) {
      finalAssets = filteredAssets;
    } else if (this.preferences()) {
      finalAssets = assetsFromPreferences;
    } else {
      finalAssets = [];
    }

    if (
      (this.preferences() || this.activeGroupFilters()?.length) &&
      !finalAssets?.length &&
      !this.searchValue()
    ) {
      this.loadingData.set(false);
      this.assets.set([]);
    } else {
      this.loadingData.set(true);

      forkJoin([
        this.assetService.getPagedAssets(
          this.currentPageIndex(),
          this.assetsPerPage(),
          finalAssets?.length ? finalAssets : null,
          this.searchValue() ? this.searchValue() : null
        ),
        this.groupService.query(),
      ]).subscribe({
        next: (x) => {
          this.loadingData.set(false);
          this.error.set(false);
          this.totalNumOfAssets.set(x[0].total);
          this.assets.set(x[0].items);
          const groups = x[1].items;

          this.assets().forEach((asset) => {
            asset.groups = groups
              .filter((g) => asset.belongsToGroups.includes(g.id))
              .map((g) => g.name);
          });

          if (!finalAssets?.length) {
            this.showCreateAssetState.set(
              this.assets()?.length === 0
            );
          }
        },
        error: () => {
          this.error.set(true);
          this.loadingData.set(false);
        },
      });
    }
  }


  private getAllowDeviceReport() {
    const { companyId } = this.authService.getUserInfo();

    this.authService.updateUserInfo();
    forkJoin([
      this.featureToggleService
        .userHasFeature(LorenFeature.DEVICE_REPORT)
        .pipe(first()),
      this.adminRolesService.getAccountRoles(companyId),
    ]).subscribe({
      next: ([userHasDeviceReportFeature, companyClientRoles]) => {
        if (!userHasDeviceReportFeature) {
          this.allowDeviceReport.set(false);
          return;
        }
        const roleClientAdminId = companyClientRoles.find(
          (i) => i.name === LOREN_USER_CLIENT_ROLES.CLIENT_ADMIN
        ).id;
        this.allowDeviceReport.set(
          this.authService
            .getUserInfo()
            .hasUserRoles.includes(roleClientAdminId)
        );
      },
      error: () => this.allowDeviceReport.set(false),
    });
  }

  private getAllowGroups() {
    this.featureToggleService.userHasFeature(LorenFeature.GROUPS).subscribe({
      next: (res) => {
        this.allowGroups.set(res);
        if (!res) {
          this.activeGroupFilters.set([]);
          this.getPagedAssets();
        }
      },
      error: () => this.allowDeviceReport.set(false),
    });
  }


  private prepareWidgetsDialog(dialogData) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.autoFocus = true;
    dialogConfig.maxWidth = "600px";
    dialogConfig.maxHeight = "800px";
    dialogConfig.data = dialogData.sort(
      (a, b) =>
        this.preferences()?.get(a.assetId)?.ordinal -
        this.preferences()?.get(b.assetId)?.ordinal
    );

    const dialogRef = this.dialog.open(
      LorenDashboardWidgetsModalComponent,
      dialogConfig
    );

    return dialogRef.afterClosed();
  }

  private getPreferences() {
    const preferences = LorenUtilsService.getDashboardWidgetsPreferences(this.userId());
    this.preferences.set(preferences);
  }
}
